import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-business-contact',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!isOpen()) {
      <button
        (click)="open()"
        class="absolute bottom-4 right-4 z-[60] bg-gradient-to-r from-pink-500 to-rose-600 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 text-sm font-semibold"
      >
        <i class="fas fa-store"></i>
        <span>{{ 'business.cta' | translate }}</span>
      </button>
    }

    @if (isOpen()) {
      <div
        class="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center backdrop-blur-sm bg-black/40"
        (click)="close()"
      >
        <div
          class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm"
          (click)="$event.stopPropagation()"
        >
          @if (submitted()) {
            <div class="p-6 text-center">
              <div
                class="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <i class="fas fa-check text-green-500 text-xl"></i>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-1">
                {{ 'business.successTitle' | translate }}
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                {{ 'business.successMsg' | translate }}
              </p>
              <button
                (click)="close()"
                class="px-5 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors text-sm font-medium"
              >
                {{ 'common.close' | translate }}
              </button>
            </div>
          } @else {
            <div class="p-5">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2.5">
                  <div
                    class="w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl flex items-center justify-center"
                  >
                    <i class="fas fa-handshake text-white text-sm"></i>
                  </div>
                  <div>
                    <h2 class="text-base font-bold text-gray-900">
                      {{ 'business.title' | translate }}
                    </h2>
                    <p class="text-xs text-gray-500">
                      {{ 'business.subtitle' | translate }}
                    </p>
                  </div>
                </div>
                <button
                  (click)="close()"
                  class="w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors"
                >
                  <i class="fas fa-times text-sm"></i>
                </button>
              </div>

              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-medium text-gray-600 mb-1"
                    >{{ 'business.businessName' | translate }} *</label
                  >
                  <input
                    type="text"
                    [(ngModel)]="company"
                    [placeholder]="
                      translate.instant('business.businessNamePlaceholder')
                    "
                    class="w-full px-3 py-3 text-base bg-gray-50 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-300 focus:bg-white transition-all placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <label class="block text-xs font-medium text-gray-600 mb-1"
                    >{{ 'business.contactMethod' | translate }} *</label
                  >
                  <div class="flex gap-2">
                    <button
                      type="button"
                      (click)="contactMethod = 'phone'"
                      class="flex-1 py-2 rounded-xl text-xs font-medium transition-all border-2 flex items-center justify-center gap-1.5"
                      [class]="
                        contactMethod === 'phone'
                          ? 'bg-pink-500 text-white border-pink-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'
                      "
                    >
                      <i class="fas fa-phone text-[10px]"></i>
                      {{ 'business.phone' | translate }}
                    </button>
                    <button
                      type="button"
                      (click)="contactMethod = 'email'"
                      class="flex-1 py-2 rounded-xl text-xs font-medium transition-all border-2 flex items-center justify-center gap-1.5"
                      [class]="
                        contactMethod === 'email'
                          ? 'bg-pink-500 text-white border-pink-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'
                      "
                    >
                      <i class="fas fa-envelope text-[10px]"></i>
                      Email
                    </button>
                  </div>
                </div>

                <div>
                  @if (contactMethod === 'phone') {
                    <input
                      type="tel"
                      [(ngModel)]="contact"
                      placeholder="+34 600 000 000"
                      class="w-full px-3 py-3 text-base bg-gray-50 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-300 focus:bg-white transition-all placeholder:text-gray-400"
                    />
                  } @else {
                    <input
                      type="email"
                      [(ngModel)]="contact"
                      placeholder="contacto@restaurante.com"
                      class="w-full px-3 py-3 text-base bg-gray-50 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-300 focus:bg-white transition-all placeholder:text-gray-400"
                    />
                  }
                </div>

                @if (errorMsg()) {
                  <div
                    class="p-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600"
                  >
                    {{ errorMsg() }}
                  </div>
                }

                <button
                  type="button"
                  (click)="submit()"
                  [disabled]="isSubmitting()"
                  class="w-full py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl text-sm font-semibold hover:from-pink-600 hover:to-rose-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  @if (isSubmitting()) {
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                    ></div>
                  } @else {
                    <i class="fas fa-paper-plane text-xs"></i>
                    <span>{{ 'business.send' | translate }}</span>
                  }
                </button>

                <p class="text-[10px] text-gray-400 text-center">
                  {{ 'business.privacyNote' | translate }}
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class BusinessContactComponent {
  isOpen = signal(false);
  isSubmitting = signal(false);
  submitted = signal(false);
  errorMsg = signal<string>('');

  company = '';
  contact = '';
  contactMethod: 'phone' | 'email' = 'phone';

  constructor(
    private http: HttpClient,
    public translate: TranslateService,
  ) {}

  open() {
    this.isOpen.set(true);
    this.submitted.set(false);
    this.errorMsg.set('');
  }

  close() {
    this.isOpen.set(false);
  }

  submit() {
    if (!this.company.trim() || !this.contact.trim()) {
      this.errorMsg.set(this.translate.instant('common.requiredFields'));
      return;
    }

    this.isSubmitting.set(true);
    this.errorMsg.set('');

    const payload = {
      name: this.company,
      company: this.company,
      email: this.contactMethod === 'email' ? this.contact : '',
      phone: this.contactMethod === 'phone' ? this.contact : '',
      preferredContactMethod: this.contactMethod,
      inquiryType: 'partnership',
      subject: `Onboarding: ${this.company}`,
      message: `Contact via ${this.contactMethod}: ${this.contact}`,
    };

    this.http
      .post(
        `${environment.apiUrl}/${environment.apiVersion}/inquiries`,
        payload,
      )
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.submitted.set(true);
          this.company = '';
          this.contact = '';
          this.contactMethod = 'phone';
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.errorMsg.set(
            this.translate.instant('common.connectionErrorMsg'),
          );
          console.error('Error submitting inquiry:', err);
        },
      });
  }
}
